/**
 * Presentational pieces shared by the Orders page, its three card types and
 * their detail modals. Nothing here fetches, decides or mutates — each piece
 * draws what it is handed, in the tokens from opsTokens.js.
 */
import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, ButtonBase, Dialog, IconButton, Typography } from '@mui/material'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined'
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import PedalBikeIcon from '@mui/icons-material/PedalBike'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RoomServiceRoundedIcon from '@mui/icons-material/RoomServiceRounded'
import {
  ops, FS, RADIUS, SHADOW, EASE, tabular, labelSx, focusRing, noMotion, tone, shimmer, dialogPaperSx, backdropSx
} from './opsTokens'
import { CATERING } from '../../utils/catering'

/* ── Chips & tags ───────────────────────────────────────────────────────── */

/** Soft tinted status chip with a 6px dot. The label always carries the meaning. */
export const StatusChip = ({ toneName, label, size = 'md', sx }) => {
  const t = tone(toneName)
  const sm = size === 'sm'
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: sm ? 20 : 24,
        px: sm ? 1 : 1.25,
        borderRadius: RADIUS.chip,
        bgcolor: t.bg,
        color: t.fg,
        fontSize: sm ? FS.label : FS.xs,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ...sx
      }}
    >
      <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.dot, flexShrink: 0 }} />
      {label}
    </Box>
  )
}

/**
 * Where an order came from (Email, RMF (old)…): a filled neutral pill.
 * `outlined` is the fulfilment style (Delivery / Pickup) — white with a border,
 * so the two kinds of tag never read as the same thing.
 */
export const Tag = ({ icon, children, outlined = false, sx }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      height: 22,
      px: 1,
      borderRadius: RADIUS.chip,
      fontSize: FS.xs,
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      flexShrink: 0,
      color: ops.text2,
      bgcolor: outlined ? ops.surface : ops.sunken,
      border: `1px solid ${outlined ? ops.borderStrong : 'transparent'}`,
      '& svg, & img': { width: 14, height: 14, flexShrink: 0, color: ops.muted },
      ...sx
    }}
  >
    {icon}
    {children}
  </Box>
)

/** Fulfilment tag: truck for any delivery, bag for pickup, plate for dine-in. */
export const FulfilmentTag = ({ kind, children }) => {
  const Icon = kind === 'pickup' ? ShoppingBagOutlinedIcon : kind === 'dinein' ? RestaurantOutlinedIcon : LocalShippingOutlinedIcon
  return <Tag outlined icon={<Icon />}>{children}</Tag>
}

/** A catering order: cloche + "Catering" in the catering accent, Tag-sized. */
export const CateringTag = () => (
  <Tag
    icon={<RoomServiceRoundedIcon />}
    sx={{
      color: CATERING.accent,
      bgcolor: CATERING.soft,
      border: `1px solid ${CATERING.border}`,
      fontWeight: 600,
      '& svg, & img': { width: 14, height: 14, flexShrink: 0, color: CATERING.accent }
    }}
  >
    Catering
  </Tag>
)

/** "+2 more" and other quiet counters. */
export const MutedChip = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex', alignItems: 'center', height: 20, px: 0.75,
      borderRadius: RADIUS.chip, bgcolor: ops.sunken, color: ops.muted,
      fontSize: FS.label, fontWeight: 600, whiteSpace: 'nowrap', ...tabular
    }}
  >
    {children}
  </Box>
)

/** Tiny caps tag used inside driver strips (CAR, STAFF, RUSHMYFOOD). */
export const MicroTag = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex', alignItems: 'center', height: 16, px: 0.6,
      borderRadius: '4px', bgcolor: 'rgba(16,24,40,0.06)', color: ops.text2,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap', flexShrink: 0
    }}
  >
    {children}
  </Box>
)

/* ── People ─────────────────────────────────────────────────────────────── */

const AVATAR_TINTS = [
  ['#EEF4FF', '#3538CD'], ['#FDF2FA', '#C11574'], ['#ECFDF3', '#067647'], ['#FEF6EE', '#B93815'],
  ['#F0F9FF', '#026AA2'], ['#F4F3FF', '#5925DC'], ['#FFFAEB', '#B54708'], ['#F0FDF9', '#107569']
]

const initialsOf = name => {
  // Words that start with a letter or digit; "—", "(" and friends are skipped.
  const words = String(name || '').trim().split(/\s+/).filter(w => /^[0-9A-Za-zÀ-῿぀-￯]/.test(w))
  if (!words.length) return '?'
  return ((words[0][0] || '') + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase()
}

const tintOf = name => {
  const s = String(name || '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AVATAR_TINTS[h % AVATAR_TINTS.length]
}

/** Initials in a circle, colour derived from the name — or the photo when there is one. */
export const InitialsAvatar = ({ name, src, size = 32 }) => {
  const [bg, fg] = tintOf(name)
  if (src) {
    return (
      <Box component="img" src={src} alt={name || ''}
        sx={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `1px solid ${ops.border}` }} />
    )
  }
  return (
    <Box aria-hidden sx={{
      width: size, height: size, borderRadius: '50%', bgcolor: bg, color: fg, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.38), fontWeight: 700, letterSpacing: '0.02em'
    }}>
      {initialsOf(name)}
    </Box>
  )
}

/** The icon for how a driver travels; a car when the provider doesn't say. */
export const VehicleIcon = ({ transport, sx }) => {
  const t = String(transport || '').toLowerCase()
  const Icon = /bike|bicycle|pedal/.test(t) ? PedalBikeIcon : /scooter|moto|two/.test(t) ? TwoWheelerIcon : DirectionsCarFilledOutlinedIcon
  return <Icon sx={{ fontSize: 16, color: ops.muted, flexShrink: 0, ...sx }} />
}

/** Monogram standing in for a delivery provider's logo (doordash → D). */
export const ProviderMark = ({ name, size = 18 }) => (
  <Box aria-hidden sx={{
    width: size, height: size, borderRadius: size > 24 ? '10px' : '5px', flexShrink: 0,
    bgcolor: ops.surface, border: `1px solid ${ops.borderStrong}`, color: ops.text2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: Math.round(size * 0.55), fontWeight: 800, textTransform: 'uppercase'
  }}>
    {String(name || '?').trim().charAt(0) || '?'}
  </Box>
)

/* ── Cards ──────────────────────────────────────────────────────────────── */

/**
 * The card frame all three order cards share: white, 14px radius, a 3px top
 * accent in the status tone, lift on hover, a focus ring for keyboard users.
 * `onOpen` is the card's existing click handler; Enter/Space call the same one.
 */
export const OrderCardShell = ({ toneName, onOpen, label, children }) => {
  const t = tone(toneName)
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={onOpen}
      onKeyDown={e => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen && onOpen() }
      }}
      sx={{
        position: 'relative',
        height: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: ops.surface,
        border: `1px solid ${ops.border}`,
        borderRadius: RADIUS.card,
        boxShadow: SHADOW.card,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: `transform 150ms ${EASE}, box-shadow 150ms ${EASE}, border-color 150ms ${EASE}`,
        '&::before': {
          content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: t.dot
        },
        '&:hover': { transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover, borderColor: ops.borderStrong },
        ...focusRing,
        ...noMotion
      }}
    >
      {children}
    </Box>
  )
}

/** Card body padding + vertical rhythm. */
export const CardBody = ({ children }) => (
  <Box sx={{ px: 2, pt: 2.25, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
    {children}
  </Box>
)

/** Pinned to the bottom of the card: hairline, then count left / total right, then extras. */
export const CardFooter = ({ left, total, children }) => (
  <Box sx={{ mt: 'auto', px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    <Box sx={{ borderTop: `1px solid ${ops.border}`, pt: 1.25, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
      <Box sx={{ minWidth: 0 }}>{left}</Box>
      <Typography sx={{ fontSize: FS.lg, fontWeight: 600, color: ops.text, lineHeight: 1.2, whiteSpace: 'nowrap', ...tabular }}>
        {total}
      </Typography>
    </Box>
    {children}
  </Box>
)

/** Header row: "#38" + relative time left, chips right. */
export const CardHeader = ({ id, time, right }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: FS.lg, fontWeight: 600, color: ops.text, lineHeight: 1.25, ...tabular }}>
        {id}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: ops.muted }}>
        {time}
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
      {right}
    </Box>
  </Box>
)

/** Customer row: avatar, name, phone. */
export const CustomerLine = ({ name, phone, fallback = '—' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
    <InitialsAvatar name={name} size={30} />
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: FS.md, fontWeight: 500, color: ops.text, lineHeight: 1.3 }}>
        {name || fallback}
      </Typography>
      {phone && (
        <Typography noWrap sx={{ fontSize: FS.sm, color: ops.muted, lineHeight: 1.3, ...tabular }}>{phone}</Typography>
      )}
    </Box>
  </Box>
)

/** Up to two lines of items, then a quiet "+N more". */
export const ItemsLine = ({ text, more }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{
      fontSize: FS.sm, color: ops.text2, lineHeight: 1.5,
      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word'
    }}>
      {text}
    </Typography>
    {more > 0 && <Box sx={{ mt: 0.5 }}><MutedChip>+{more} more</MutedChip></Box>}
  </Box>
)

/** Small tinted callout: scheduled-for-later, unpaid, "Rider on the way"… */
export const Callout = ({ toneName, icon, children, center = false, sx }) => {
  const t = tone(toneName)
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: center ? 'center' : 'flex-start', gap: 0.75,
      px: 1.25, py: 0.75, borderRadius: '8px', bgcolor: t.bg, color: t.fg, minWidth: 0,
      '& svg': { fontSize: 15, flexShrink: 0 }, ...sx
    }}>
      {icon}
      <Typography component="span" noWrap={!center} sx={{ fontSize: FS.xs, fontWeight: 600, color: 'inherit', lineHeight: 1.35, minWidth: 0, ...tabular }}>
        {children}
      </Typography>
    </Box>
  )
}

/** Compact driver strip on a card. */
export const DriverStrip = ({ children, sx }) => (
  <Box sx={{
    px: 1.25, py: 1, borderRadius: '10px', bgcolor: ops.subtle, border: `1px solid ${ops.border}`,
    display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, ...sx
  }}>
    {children}
  </Box>
)

/** Link styled as a small secondary button with the external-link icon. */
export const LinkChip = ({ href, children, icon, sx, ...rest }) => (
  <Box
    component="a"
    href={href}
    {...rest}
    sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
      height: 26, px: 1, borderRadius: '8px', textDecoration: 'none', whiteSpace: 'nowrap',
      bgcolor: ops.surface, color: ops.text2, border: `1px solid ${ops.borderStrong}`,
      fontSize: FS.xs, fontWeight: 600, ...tabular,
      transition: `background-color 150ms ${EASE}`,
      '&:hover': { bgcolor: ops.sunken },
      '& svg': { fontSize: 13, color: ops.muted },
      ...focusRing,
      ...sx
    }}
  >
    {children}
    {icon}
  </Box>
)

/* ── Filters ────────────────────────────────────────────────────────────── */

/**
 * One filter pill: neutral until chosen, then filled dark. `dot` is the status
 * colour; the label and count always say what it is, so colour is never the
 * only signal.
 */
export const FilterPill = ({ active, dot, icon, label, count, onClick }) => {
  // In a sideways-scrolling row, a pill chosen from elsewhere (a KPI tile) can
  // sit half off the edge; bring it into view when it becomes the active one.
  // Scrolls only its own row, sideways — never the page (scrollIntoView would
  // pull a phone's page down to the pills on load).
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    const row = el && el.parentElement
    if (!active || !row || row.scrollWidth <= row.clientWidth) return
    const r = el.getBoundingClientRect()
    const p = row.getBoundingClientRect()
    const pad = 28 // clear of the row's faded edge
    if (r.left < p.left + pad) row.scrollLeft -= p.left + pad - r.left
    else if (r.right > p.right - pad) row.scrollLeft += r.right - (p.right - pad)
  }, [active])
  return (
  <ButtonBase
    ref={ref}
    onClick={onClick}
    aria-pressed={active}
    sx={{
      flexShrink: 0,
      height: 32,
      pl: 1.125,
      pr: 0.5,
      gap: 0.5,
      borderRadius: RADIUS.chip,
      border: `1px solid ${active ? ops.ink : ops.border}`,
      bgcolor: active ? ops.ink : ops.surface,
      color: active ? '#fff' : ops.text2,
      fontFamily: 'inherit',
      fontSize: FS.sm,
      fontWeight: active ? 600 : 500,
      whiteSpace: 'nowrap',
      boxShadow: active ? 'none' : SHADOW.pop,
      transition: `background-color 150ms ${EASE}, border-color 150ms ${EASE}, color 150ms ${EASE}`,
      '&:hover': { bgcolor: active ? ops.inkHover : ops.subtle, borderColor: active ? ops.inkHover : ops.borderStrong },
      '& svg': { fontSize: 15, color: active ? '#fff' : ops.muted },
      ...focusRing,
      ...noMotion
    }}
  >
    {dot && <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />}
    {icon}
    <span>{label}</span>
    <Box
      component="span"
      sx={{
        minWidth: 18, height: 20, px: 0.5, borderRadius: RADIUS.chip,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: FS.label, fontWeight: 600, ...tabular,
        bgcolor: active ? 'rgba(255,255,255,0.16)' : ops.sunken,
        color: active ? '#fff' : ops.muted
      }}
    >
      {count}
    </Box>
  </ButtonBase>
  )
}

/** A row that scrolls sideways on small screens, with soft fade edges. */
export const ScrollRow = ({ children, label }) => (
  <Box
    role="group"
    aria-label={label}
    sx={{
      display: 'flex',
      gap: 0.5,
      overflowX: 'auto',
      mx: { xs: -2, md: -3 },
      px: { xs: 2, md: 3 },
      py: 0.5,
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      WebkitMaskImage: {
        xs: 'linear-gradient(to right, transparent 0, #000 16px, #000 calc(100% - 24px), transparent 100%)',
        md: 'linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)'
      },
      maskImage: {
        xs: 'linear-gradient(to right, transparent 0, #000 16px, #000 calc(100% - 24px), transparent 100%)',
        md: 'linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)'
      }
    }}
  >
    {children}
  </Box>
)

/** Compact segmented control. `options` = [{ value, label }]. */
export const Segmented = ({ options, value, onSelect, label }) => (
  <Box
    role="group"
    aria-label={label}
    sx={{ display: 'inline-flex', p: '3px', gap: '2px', bgcolor: ops.sunken, borderRadius: RADIUS.button, maxWidth: '100%', overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
  >
    {options.map(o => {
      const active = o.value === value
      return (
        <ButtonBase
          key={o.value}
          onClick={() => onSelect(o.value)}
          aria-pressed={active}
          sx={{
            flexShrink: 0, height: 28, px: 1.25, borderRadius: '8px', fontFamily: 'inherit',
            fontSize: FS.sm, fontWeight: active ? 600 : 500, whiteSpace: 'nowrap',
            color: active ? ops.text : ops.muted,
            bgcolor: active ? ops.surface : 'transparent',
            boxShadow: active ? '0 1px 2px rgba(16,24,40,.08), 0 0 0 1px rgba(16,24,40,.04)' : 'none',
            transition: `background-color 150ms ${EASE}, color 150ms ${EASE}`,
            '&:hover': { color: ops.text },
            ...focusRing,
            ...noMotion
          }}
        >
          {o.label}
        </ButtonBase>
      )
    })}
  </Box>
)

/* ── Loading & empty ────────────────────────────────────────────────────── */

const bone = (w, h, r = 6) => ({
  width: w, height: h, borderRadius: `${r}px`,
  background: `linear-gradient(90deg, ${'#F2F4F7'} 0px, ${'#FAFAFB'} 80px, ${'#F2F4F7'} 160px)`,
  backgroundSize: '640px 100%',
  animation: `${shimmer} 1.3s linear infinite`,
  ...noMotion
})

/** Placeholder with the same anatomy as a real card. */
export const OrderCardSkeleton = () => (
  <Box aria-hidden sx={{
    bgcolor: ops.surface, border: `1px solid ${ops.border}`, borderRadius: RADIUS.card,
    boxShadow: SHADOW.card, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 236
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={bone(72, 16)} />
        <Box sx={bone(56, 10)} />
      </Box>
      <Box sx={bone(76, 22, 11)} />
    </Box>
    <Box sx={{ display: 'flex', gap: 0.75 }}>
      <Box sx={bone(64, 20, 10)} />
      <Box sx={bone(84, 20, 10)} />
    </Box>
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
      <Box sx={bone(30, 30, 15)} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={bone(110, 12)} />
        <Box sx={bone(80, 10)} />
      </Box>
    </Box>
    <Box sx={bone('100%', 12)} />
    <Box sx={bone('70%', 12)} />
    <Box sx={{ mt: 'auto', pt: 1.25, borderTop: `1px solid ${ops.border}`, display: 'flex', justifyContent: 'space-between' }}>
      <Box sx={bone(48, 12)} />
      <Box sx={bone(64, 16)} />
    </Box>
  </Box>
)

/** Centered icon, a short title, a muted line, and whatever action already existed. */
export const EmptyState = ({ icon, title, message, action }) => (
  <Box sx={{
    textAlign: 'center', py: { xs: 7, md: 9 }, px: 2,
    bgcolor: ops.surface, border: `1px dashed ${ops.borderStrong}`, borderRadius: RADIUS.card
  }}>
    <Box sx={{
      width: 56, height: 56, mx: 'auto', mb: 2, borderRadius: '16px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: ops.sunken, color: ops.muted, '& svg': { fontSize: 28 }
    }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: FS.lg, fontWeight: 600, color: ops.text }}>{title}</Typography>
    <Typography sx={{ fontSize: FS.sm, color: ops.muted, mt: 0.5, maxWidth: 420, mx: 'auto' }}>{message}</Typography>
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>
)

/* ── Modal pieces ───────────────────────────────────────────────────────── */

/** Uppercase label over a value, for the modals' definition grids. */
export const InfoField = ({ label, icon, children, sx }) => (
  <Box sx={{ minWidth: 0, ...sx }}>
    <Typography component="div" sx={{ ...labelSx, mb: 0.5 }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
      {icon && <Box sx={{ color: ops.faint, display: 'flex', mt: '2px', '& svg': { fontSize: 16 } }}>{icon}</Box>}
      <Box sx={{ minWidth: 0, fontSize: 15, fontWeight: 500, color: ops.text, lineHeight: 1.45 }}>{children}</Box>
    </Box>
  </Box>
)

export const InfoGrid = ({ children, sx }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
    gap: 2.25, ...sx
  }}>
    {children}
  </Box>
)

/**
 * The driver / delivery-status card, one design for every order type: a quiet
 * header strip (what kind of driver, where they are), then the body.
 */
export const DriverCard = ({ label, status, children }) => (
  <Box sx={{ border: `1px solid ${ops.border}`, borderRadius: RADIUS.tile, overflow: 'hidden', bgcolor: ops.surface }}>
    <Box sx={{
      px: 1.5, py: 1, bgcolor: ops.subtle, borderBottom: `1px solid ${ops.border}`,
      display: 'flex', alignItems: 'center', gap: 1, minHeight: 40
    }}>
      <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: ops.muted }} />
      <Typography component="div" sx={{ ...labelSx, flex: 1, minWidth: 0 }}>{label}</Typography>
      {status}
    </Box>
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>{children}</Box>
  </Box>
)

/** Avatar, name + tags, a muted line under it, and an action on the right. */
export const DriverIdentity = ({ avatar, name, tags, sub, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
    {avatar}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 15, fontWeight: 600, color: ops.text, minWidth: 0 }}>{name}</Typography>
        {tags}
      </Box>
      {sub && (
        <Typography component="div" sx={{ fontSize: FS.sm, color: ops.muted, mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ...tabular }}>
          {sub}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
)

/** Soft green Call button; a plain tel: link. */
export const CallButton = ({ href, label = 'Call' }) => (
  <Box
    component="a"
    href={href}
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
      height: 34, px: 1.5, borderRadius: RADIUS.button, textDecoration: 'none',
      bgcolor: tone('emerald').bg, color: tone('emerald').fg, fontSize: FS.sm, fontWeight: 600,
      transition: `background-color 150ms ${EASE}`,
      '&:hover': { bgcolor: 'rgba(23,178,106,0.2)' },
      '& svg': { fontSize: 16 },
      ...focusRing
    }}
  >
    <PhoneOutlinedIcon />
    {label}
  </Box>
)

/** A pickup / dropoff ETA as a mini stat tile. */
export const EtaTile = ({ label, value }) => (
  <Box sx={{ flex: 1, minWidth: 0, p: 1.25, borderRadius: RADIUS.tile, bgcolor: ops.subtle, border: `1px solid ${ops.border}` }}>
    <Typography sx={{ ...labelSx }}>{label}</Typography>
    {/* A bare time reads large; a full date-time steps down so it fits. */}
    <Typography sx={{ fontSize: String(value).length > 10 ? FS.lg : FS.xl, fontWeight: 600, color: ops.text, mt: 0.25, lineHeight: 1.25, ...tabular }}>{value}</Typography>
  </Box>
)

/** Section heading inside a modal body. */
export const SectionTitle = ({ children, right }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
    <Typography component="div" sx={{ ...labelSx }}>{children}</Typography>
    {right}
  </Box>
)

/** One item row: qty box, name + modifiers, price right. `extra` spans the
    name and price columns, for lines that carry their own right-hand price. */
export const ItemRow = ({ qty, children, price, extra, last, sx }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '32px minmax(0,1fr) auto', columnGap: 1.25, alignItems: 'start',
    py: 1.25, borderBottom: last ? 'none' : `1px solid ${ops.border}`, ...sx
  }}>
    <Box sx={{
      height: 24, minWidth: 28, px: 0.5, borderRadius: '6px', bgcolor: ops.sunken, color: ops.text2,
      fontSize: FS.xs, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', ...tabular
    }}>
      {qty}
    </Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
    <Box sx={{ textAlign: 'right', ...tabular }}>{price}</Box>
    {extra && <Box sx={{ gridColumn: '2 / 4', minWidth: 0 }}>{extra}</Box>}
  </Box>
)

/** A modifier line under an item: muted 13px, indented. */
export const ModLine = ({ children, indent = 0, italic = false }) => (
  <Typography component="div" sx={{
    fontSize: FS.sm, color: ops.muted, lineHeight: 1.45, pl: indent, fontStyle: italic ? 'italic' : 'normal', ...tabular
  }}>
    {children}
  </Typography>
)

/** A label/value row in a totals block. */
export const SumRow = ({ label, value, toneName, strong = false }) => {
  const c = toneName ? tone(toneName).fg : null
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.4 }}>
      <Typography sx={{ fontSize: FS.md, color: c || (strong ? ops.text : ops.muted), fontWeight: strong ? 600 : 400 }}>{label}</Typography>
      <Typography sx={{ fontSize: FS.md, color: c || ops.text, fontWeight: strong || c ? 600 : 500, whiteSpace: 'nowrap', ...tabular }}>{value}</Typography>
    </Box>
  )
}

export const TotalRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: 1.25, mt: 0.75, borderTop: `1px solid ${ops.border}` }}>
    <Typography sx={{ fontSize: FS.lg, fontWeight: 600, color: ops.text }}>{label}</Typography>
    <Typography sx={{ fontSize: FS.xl, fontWeight: 700, color: ops.text, ...tabular }}>{value}</Typography>
  </Box>
)

/* ── Photos ─────────────────────────────────────────────────────────────── */

/**
 * A photo at full size over the order, instead of a new tab — some photo hosts
 * serve the file as a download, and a new tab loses the order you were reading.
 * `contain`, because the whole frame is the point: the house number, the door.
 * "Open original" stays for anyone who wants the file itself.
 */
export const PhotoLightbox = ({ open, onClose, src, title = 'PROOF OF DELIVERY', alt = 'Proof of delivery' }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    PaperProps={{ sx: { ...dialogPaperSx, bgcolor: '#101828', backgroundImage: 'none' } }}
    BackdropProps={{ sx: backdropSx }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, gap: 0.5 }}>
      <Typography sx={{ flex: 1, color: '#fff', fontWeight: 600, fontSize: FS.label, letterSpacing: '0.08em' }}>
        {title}
      </Typography>
      <Button
        size="small"
        href={src || ''}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
        sx={{ textTransform: 'none', color: '#D0D5DD', borderRadius: RADIUS.button, fontWeight: 600, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
      >
        Open original
      </Button>
      <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: '#fff', borderRadius: RADIUS.button, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box sx={{ px: 2, pb: 2 }}>
      <img
        src={src}
        alt={alt}
        style={{ display: 'block', maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', margin: '0 auto', borderRadius: 12 }}
      />
    </Box>
  </Dialog>
)

/** Square thumbnail that opens PhotoLightbox. `width` defaults to filling its cell. */
export const ZoomablePhoto = ({ src, alt, title, width = '100%' }) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <ButtonBase
        onClick={() => setOpen(true)}
        aria-label={`Enlarge ${alt}`}
        sx={{
          position: 'relative', display: 'block', width, maxWidth: '100%', borderRadius: RADIUS.tile,
          overflow: 'hidden', border: `1px solid ${ops.border}`, cursor: 'zoom-in',
          '& img': { transition: `transform 200ms ${EASE}` },
          '&:hover img': { transform: 'scale(1.04)' },
          '&:hover .rmf-zoom-hint, &.Mui-focusVisible .rmf-zoom-hint': { opacity: 1 },
          ...focusRing,
          ...noMotion
        }}
      >
        <img src={src} alt={alt} style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }} />
        <Box
          className="rmf-zoom-hint"
          sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            pb: 0.75, opacity: 0, transition: 'opacity 0.15s',
            background: 'linear-gradient(to top, rgba(16,24,40,0.75), transparent 55%)'
          }}
        >
          <Typography sx={{ fontSize: FS.label, color: '#fff', fontWeight: 600 }}>Click to enlarge</Typography>
        </Box>
      </ButtonBase>
      <PhotoLightbox open={open} onClose={() => setOpen(false)} src={src} title={title} alt={alt} />
    </>
  )
}
